import { isExpiredExpiryDate } from "./medicineExpiryThreshold";

/** Available quantity at or below this value is treated as low stock (includes 0 = out of stock). */
export const DEFAULT_LOW_STOCK_QUANTITY_THRESHOLD = 10;

export const LOW_STOCK_ALERT_SESSION_PREFIX = "hms_low_stock_alert_shown";

export const getLowStockQuantityThresholdStorageKey = (organizationId) =>
  organizationId
    ? `hms_pharmacy_low_stock_quantity_threshold_${organizationId}`
    : "hms_pharmacy_low_stock_quantity_threshold_global";

export const readLowStockQuantityThreshold = (organizationId) => {
  try {
    const stored = localStorage.getItem(
      getLowStockQuantityThresholdStorageKey(organizationId)
    );
    if (stored == null || stored === "") return DEFAULT_LOW_STOCK_QUANTITY_THRESHOLD;
    const value = Number(stored);
    return Number.isFinite(value) && value >= 0
      ? value
      : DEFAULT_LOW_STOCK_QUANTITY_THRESHOLD;
  } catch {
    return DEFAULT_LOW_STOCK_QUANTITY_THRESHOLD;
  }
};

export const getLowStockAlertSessionKey = (userId) =>
  `${LOW_STOCK_ALERT_SESSION_PREFIX}_${userId ?? "unknown"}`;

export const hasShownLowStockAlertThisSession = (userId) =>
  sessionStorage.getItem(getLowStockAlertSessionKey(userId)) === "1";

export const markLowStockAlertShown = (userId) => {
  sessionStorage.setItem(getLowStockAlertSessionKey(userId), "1");
};

export const clearLowStockAlertSession = (userId) => {
  sessionStorage.removeItem(getLowStockAlertSessionKey(userId));
};

export const canShowPharmacyLowStockAlert = (roleName) => {
  const role = String(roleName || "").toLowerCase();
  return ["pharmacy admin", "admin", "superadmin", "super_admin"].includes(role);
};

/**
 * Build low-stock list from raw inventory rows (non-expired qty grouped by medicine).
 */
export const buildLowStockMedicineList = (
  inventoryItems,
  quantityThreshold = DEFAULT_LOW_STOCK_QUANTITY_THRESHOLD
) => {
  const grouped = new Map();

  (inventoryItems || []).forEach((item) => {
    const expiryDate =
      item.expirationDate || item.expiryDate || item.expiry || item.expirydate;
    if (isExpiredExpiryDate(expiryDate)) return;

    const medicineId = item.medicineId ?? item.medicine_id;
    const name = item.name || item.medicineName || "Unknown";
    const key = medicineId != null ? `id-${medicineId}` : `name-${name}`;
    const qty = parseInt(item.quantity, 10) || 0;

    if (!grouped.has(key)) {
      grouped.set(key, {
        medicineId: medicineId ?? null,
        name,
        totalQuantity: 0,
        batchCount: 0,
        manufacturer:
          item.manufacturer || item.medicineManufacturer || item.supplier || null,
        batches: [],
      });
    }

    const entry = grouped.get(key);
    entry.totalQuantity += qty;
    entry.batchCount += 1;
    if (!entry.manufacturer) {
      entry.manufacturer =
        item.manufacturer || item.medicineManufacturer || item.supplier || null;
    }
    const batchNumber = item.batchNumber || item.batch_number;
    if (batchNumber) {
      entry.batches.push({
        batchNumber,
        quantity: qty,
        expiryDate:
          item.expirationDate || item.expiryDate || item.expiry || item.expirydate,
      });
    }
  });

  const pickDisplayBatchNumber = (batches) => {
    if (!batches?.length) return null;
    const sorted = [...batches].sort((a, b) => {
      const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
      const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
      return dateA - dateB;
    });
    return sorted[0].batchNumber;
  };

  return Array.from(grouped.values())
    .filter((m) => m.totalQuantity <= quantityThreshold)
    .sort((a, b) => a.totalQuantity - b.totalQuantity)
    .map((m) => ({
      medicineId: m.medicineId,
      name: m.name,
      totalQuantity: m.totalQuantity,
      batchCount: m.batchCount,
      manufacturer: m.manufacturer || null,
      batchNumber: pickDisplayBatchNumber(m.batches),
      status: m.totalQuantity === 0 ? "Out of stock" : "Low stock",
    }));
};

export const normalizeLowStockMedicineList = (data) => {
  const raw =
    data?.medicines ??
    data?.lowStockMedicines ??
    data?.items ??
    (Array.isArray(data) ? data : []);

  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => ({
      medicineId: item.medicineId ?? item.id ?? null,
      name: item.name || item.medicineName || "Unknown",
      totalQuantity:
        item.totalQuantity ??
        item.quantity ??
        item.availableQuantity ??
        0,
      batchCount: item.batchCount ?? item.batch_count ?? null,
      batchNumber: item.batchNumber || item.batch_number || null,
      manufacturer: item.manufacturer || item.medicineManufacturer || null,
      status:
        item.status ||
        (Number(item.totalQuantity ?? item.quantity) === 0
          ? "Out of stock"
          : "Low stock"),
    }))
    .sort((a, b) => a.totalQuantity - b.totalQuantity);
};

export const getOutOfStockMedicines = (medicines = []) =>
  medicines.filter((m) => Number(m.totalQuantity) === 0);

export const countOutOfStockMedicines = (medicines = []) =>
  getOutOfStockMedicines(medicines).length;

/** Map low-stock rows for dashboard inventory / OOS widgets. */
export const mapLowStockToDashboardInventoryRows = (medicines = [], limit = 5) =>
  medicines.slice(0, limit).map((m, index) => ({
    id: m.medicineId ?? `low-stock-${index}`,
    name: m.name,
    quantity: m.totalQuantity,
    manufacturer: m.manufacturer || null,
    batchNumber:
      m.batchNumber ||
      (m.batchCount > 0 ? `${m.batchCount} batch(es)` : null),
    status: m.status || (m.totalQuantity === 0 ? "Out of stock" : "Low stock"),
  }));
